/**
 * 구역 좌석 고르기 — 배치도 구역과 순찰 QR 구역이 같이 쓴다 (2026-10-04)
 *
 * 구역의 좌석은 좌석마다 하나(seats.zone_id)만 저장된다. 배치도에서 고치든 순찰 QR 화면에서
 * 고치든 같은 곳에 저장되므로, 한쪽에서 바꾸면 다른 쪽에도 바로 보인다.
 * 좌석은 한 구역에만 들어간다 — 다른 구역 좌석을 고르면 이 구역으로 옮겨진다.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';
import { authedFetch } from '../../../../infrastructure/api/authedFetch';

export interface RoomInfo { id: string; name: string; display_order?: number }
export interface SeatInfo { seat_number: number; room_id: string; is_out_of_order?: boolean }

export const MOVE_HINT = '좌석을 눌러 켜고 끕니다. 회색 점 = 이미 다른 구역에 들어 있는 좌석 (고르면 이 구역으로 옮겨집니다).';

/** 방별로 좌석 번호를 눌러 켜고 끈다. 글자로 "1-30, 45" 를 치면 틀리기 쉬워서 실제 좌석을 보고 고르게 한다. */
export function SeatPicker({ rooms, seats, selected, onChange, takenBy, hint = MOVE_HINT }: {
  rooms: RoomInfo[];
  seats: SeatInfo[];
  selected: Set<number>;
  onChange: (next: Set<number>) => void;
  takenBy: Map<number, string>;
  hint?: string;
}) {
  const groups: { id: string; name: string; seats: SeatInfo[] }[] = [];
  const byRoom = new Map<string, SeatInfo[]>();
  seats.forEach((st) => {
    const k = st.room_id || '';
    if (!byRoom.has(k)) byRoom.set(k, []);
    byRoom.get(k)!.push(st);
  });
  [...rooms].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)).forEach((r) => {
    const list = byRoom.get(r.id);
    if (list && list.length) groups.push({ id: r.id, name: r.name, seats: list });
    byRoom.delete(r.id);
  });
  byRoom.forEach((list, k) => { if (list.length) groups.push({ id: k || 'none', name: '방 미지정 좌석', seats: list }); });
  groups.forEach((g) => g.seats.sort((a, b) => a.seat_number - b.seat_number));

  const setMany = (nums: number[], on: boolean) => {
    const next = new Set(selected);
    nums.forEach((n) => (on ? next.add(n) : next.delete(n)));
    onChange(next);
  };
  const chip = (on: boolean): React.CSSProperties => ({
    minWidth: 44, padding: '6px 0', borderRadius: 8, fontSize: 13, cursor: 'pointer', position: 'relative',
    border: on ? '1px solid #2563eb' : '1px solid #d1d5db',
    background: on ? '#2563eb' : '#fff', color: on ? '#fff' : '#374151', fontWeight: on ? 700 : 500,
  });
  const small: React.CSSProperties = { padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12 };

  return (
    <div style={{ marginTop: 10 }}>
      {groups.map((g) => {
        const nums = g.seats.map((x) => x.seat_number);
        const onCount = nums.filter((n) => selected.has(n)).length;
        return (
          <div key={g.id} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{g.name}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>{onCount}/{nums.length}석</span>
              <button type="button" style={small} onClick={() => setMany(nums, true)}>전체 선택</button>
              <button type="button" style={small} onClick={() => setMany(nums, false)}>해제</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {g.seats.map((st) => {
                const on = selected.has(st.seat_number);
                const other = takenBy.get(st.seat_number);
                return (
                  <button type="button" key={st.seat_number} style={chip(on)}
                    title={other ? `지금 '${other}' 구역에 들어 있는 좌석` : undefined}
                    onClick={() => setMany([st.seat_number], !on)}>
                    {st.seat_number}
                    {other && !on && (
                      <span style={{ position: 'absolute', top: 3, right: 4, width: 6, height: 6, borderRadius: 3, background: '#9ca3af' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <div style={{ fontSize: 12, color: '#9ca3af' }}>{hint}</div>
    </div>
  );
}

const api = (path: string, init?: RequestInit) => authedFetch(`${API_BASE_URL}${path}`, init);

/** 매장 좌석·방 목록 (순찰 QR 화면과 같은 출처) */
export async function loadStoreSeats(): Promise<{ seats: SeatInfo[]; rooms: RoomInfo[] }> {
  const [sr, rr] = await Promise.all([api('/api/v1/owner/seats-management'), api('/api/v1/owner/rooms')]);
  if (!sr.ok) throw new Error('좌석 목록을 불러오지 못했습니다');
  const sd = await sr.json();
  const seats: SeatInfo[] = (sd.seats || [])
    .filter((x: any) => Number.isFinite(Number(x.seat_number)))
    .map((x: any) => ({ seat_number: Number(x.seat_number), room_id: x.room_id || '', is_out_of_order: x.is_out_of_order }));
  let rooms: RoomInfo[] = [];
  if (rr.ok) { const rd = await rr.json(); rooms = Array.isArray(rd) ? rd : (rd.rooms || []); }
  return { seats, rooms };
}

interface ZoneRow { id: string; name: string; seat_numbers?: number[]; has_patrol_qr?: boolean }

/** 배치도 구역 하나의 좌석을 고르는 창 */
export function ZoneSeatsModal({ zone, onClose, onSaved }: {
  zone: { id: string; name: string; color?: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [seats, setSeats] = useState<SeatInfo[]>([]);
  const [rooms, setRooms] = useState<RoomInfo[]>([]);
  const [allZones, setAllZones] = useState<ZoneRow[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [store, zr] = await Promise.all([loadStoreSeats(), api('/api/v1/seat-config/zones')]);
        if (!alive) return;
        setSeats(store.seats); setRooms(store.rooms);
        const zones: ZoneRow[] = zr.ok ? await zr.json() : [];
        setAllZones(zones);
        const mine = zones.find((z) => String(z.id) === String(zone.id));
        setPicked(new Set(mine?.seat_numbers || []));
      } catch (e: any) {
        if (alive) setErr(e?.message || '불러오지 못했습니다');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [zone.id]);

  const takenBy = useMemo(() => {
    const m = new Map<number, string>();
    allZones.forEach((z) => {
      if (String(z.id) === String(zone.id)) return;
      (z.seat_numbers || []).forEach((n) => { if (!m.has(n)) m.set(n, z.name); });
    });
    return m;
  }, [allZones, zone.id]);
  const mine = allZones.find((z) => String(z.id) === String(zone.id));

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const r = await api(`/api/v1/seat-config/zones/${zone.id}/seats`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seat_numbers: Array.from(picked).sort((a, b) => a - b) }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d?.detail === 'string' ? d.detail : '저장하지 못했습니다');
      }
      onSaved();
    } catch (e: any) {
      setErr(e?.message || '저장하지 못했습니다');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: zone.color || '#3B82F6' }} />
            {zone.name} — 좌석 지정
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl leading-none">×</button>
        </div>
        {mine?.has_patrol_qr && (
          <div className="mb-2 text-sm text-blue-700 bg-blue-50 rounded px-3 py-2">
            🚶 이 구역에는 순찰 QR 이 붙어 있습니다. 여기서 좌석을 바꾸면 순찰 점검표에도 바로 반영됩니다.
          </div>
        )}
        {loading ? (
          <div className="text-gray-500 py-6">불러오는 중…</div>
        ) : (
          <SeatPicker rooms={rooms} seats={seats} selected={picked} onChange={setPicked} takenBy={takenBy} />
        )}
        {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm font-semibold">선택한 좌석 {picked.size}개</span>
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">취소</button>
          <button onClick={save} disabled={saving || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {saving ? '저장 중…' : '좌석 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
